/**
 * #979 — the Source control in a card header: a labelled button and its menu.
 *
 * Renders nothing outside a `CardSourcesProvider` (see that file). Inside one
 * it sits in the `PinToggle`/`CardNoteControl` mould: every click stops
 * propagation so choosing a source never opens the order ticket, pins,
 * cancels or trades, and the trigger carries `data-lt2-owner` so no hotkey
 * layer treats it as a trade key. The menu is `role="menu"`, which the LT2
 * listener already treats as owning its keystrokes.
 *
 * Menu order (contract §1): + Add New, the existing names alphabetically,
 * Clear. "+ Add New" opens the provider's one add dialog; once a name is
 * added this menu reopens so the new name can be seen — and chosen, or not.
 * Adding never selects.
 */
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { useStore } from 'zustand';
import { useDismissibleLayer } from '@/hooks/ui/useDismissibleLayer';
import { knownSources, type CardSourcesController, type WriteIssue } from '@/lib/cardSources/cardSourcesController';
import { sortSources } from '@/lib/cardSources/sourceName';
import { targetKey, withScope, type CardSourceSubject, type CardSourceTarget } from '@/lib/cardSources/types';
import { useCardSources, type AddSourceRequest } from './CardSourcesProvider';

export function CardSourceControl({ subject }: { subject: CardSourceSubject }) {
  const sources = useCardSources();
  if (!sources) return null;
  return (
    <SourceButton
      controller={sources.controller}
      scope={sources.scope}
      subject={subject}
      openAddSource={sources.openAddSource}
    />
  );
}

/** What the button can honestly say about this card's source. */
type LabelState =
  | { kind: 'unavailable' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'none' }
  | { kind: 'named'; name: string }
  /** Assigned, but the name list failed to load: we can't say which name. */
  | { kind: 'listError' }
  | { kind: 'unknown' };

const ISSUE_TEXT: Record<WriteIssue, string> = {
  failed: 'Couldn’t save the source. Try again.',
  forbidden: 'You don’t have permission to change this card’s source.',
  unknown_source: 'That source no longer exists. Choose another.',
  conflict: 'This card’s source was changed elsewhere. Showing the latest.',
};

/**
 * A pointer choice closes the menu, so the second click of a double-click
 * would land on whatever the menu covered — on the Tracker, a card whose click
 * opens the order ticket. Swallow exactly that follow-up click (it arrives
 * with `detail >= 2` within the double-click interval), and nothing else.
 */
function swallowFollowUpClick() {
  const stop = (e: MouseEvent) => {
    if (e.detail < 2) return;
    e.preventDefault();
    e.stopPropagation();
  };
  window.addEventListener('mousedown', stop, true);
  window.addEventListener('click', stop, true);
  window.setTimeout(() => {
    window.removeEventListener('mousedown', stop, true);
    window.removeEventListener('click', stop, true);
  }, 600);
}

const MENU_WIDTH = 224; // matches w-56 below — keep in sync
const MENU_GAP = 4;
/** Below this much room under the trigger, the menu opens upward. */
const MIN_ROOM_BELOW = 200;

type MenuPlacement = { left: number; top?: number; bottom?: number; maxHeight: number };

function placeMenu(trigger: HTMLElement): MenuPlacement {
  const r = trigger.getBoundingClientRect();
  const left = Math.min(Math.max(8, r.right - MENU_WIDTH), window.innerWidth - MENU_WIDTH - 8);
  const below = window.innerHeight - r.bottom - MENU_GAP - 8;
  const above = r.top - MENU_GAP - 8;
  if (below >= MIN_ROOM_BELOW || below >= above) return { left, top: r.bottom + MENU_GAP, maxHeight: below };
  return { left, bottom: window.innerHeight - r.top + MENU_GAP, maxHeight: above };
}

function SourceButton({
  controller,
  scope,
  subject,
  openAddSource,
}: {
  controller: CardSourcesController;
  scope: string | null;
  subject: CardSourceSubject;
  openAddSource: (request: AddSourceRequest) => void;
}) {
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Tagged with the target AND the scope generation it opened in: any account
  // switch or logout bumps the generation, which closes the menu for good —
  // returning to the same account (A→B→A) cannot bring it back.
  const [placement, setPlacement] = useState<(MenuPlacement & { key: string; generation: number }) | null>(null);
  const generation = useStore(controller.store, (s) => s.generation);

  // Keyed on primitives: `subject` is usually a fresh literal each render, and
  // a new target identity would re-register (and re-read) on every tick.
  const id = subject.kind === 'order' ? subject.venueOrderId : subject.marketId;
  const outcome = subject.kind === 'position' ? subject.outcome : null;
  const target: CardSourceTarget | null = useMemo(
    () => (scope == null ? null : withScope(subject, scope)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `subject` is covered by its primitives
    [scope, subject.kind, subject.venue, id, outcome],
  );
  const key = target ? targetKey(target) : null;
  const open = placement != null && placement.key === key && placement.generation === generation;

  useEffect(() => {
    if (target) controller.register(target);
  }, [controller, target]);

  const entry = useStore(controller.store, (s) => (key ? s.entries[key] : undefined));
  const dictionary = useStore(controller.store, (s) => s.dictionary);
  const writing = useStore(controller.store, (s) => key != null && s.writing[key] === true);
  const issue = useStore(controller.store, (s) => (key ? s.issues[key] : undefined));

  const known = knownSources(dictionary);
  const assignedId = entry?.status === 'ready' ? entry.assignment.sourceId : null;
  const label: LabelState = !target || entry?.status === 'unavailable'
    ? { kind: 'unavailable' }
    : !entry || entry.status === 'loading'
      ? { kind: 'loading' }
      : entry.status === 'error'
        ? { kind: 'error' }
        : assignedId === null
          ? { kind: 'none' }
          : (() => {
              const hit = known?.find((s) => s.id === assignedId);
              if (hit) return { kind: 'named', name: hit.name } as const;
              // Only a list that actually loaded can say a name is gone.
              if (dictionary.status === 'ready') return { kind: 'unknown' } as const;
              if (dictionary.status === 'error') return { kind: 'listError' } as const;
              return { kind: 'loading' } as const;
            })();

  const buttonText =
    label.kind === 'named'
      ? label.name
      : label.kind === 'unknown'
        ? 'Unknown'
        : label.kind === 'listError'
          ? 'Not loaded'
          : 'Source';
  const title =
    label.kind === 'unavailable'
      ? target
        ? "Source unavailable: this card's account could not be confirmed"
        : 'Source unavailable — this card has no resolved account yet'
      : label.kind === 'loading'
        ? 'Loading source…'
        : label.kind === 'error'
          ? 'Couldn’t load this card’s source — click to retry'
          : writing
            ? 'Saving source…'
            : issue
              ? `${ISSUE_TEXT[issue]} Click for details.`
              : label.kind === 'listError'
                ? 'This card has a source, but your source names couldn’t load — click to retry'
            : label.kind === 'named'
              ? `Source: ${label.name} — click to change or clear`
              : label.kind === 'unknown'
                ? 'This card’s source is no longer in your list — click to change or clear'
                : 'Choose a source for this card';
  const ariaLabel =
    label.kind === 'named'
      ? `Source: ${label.name}`
      : label.kind === 'unknown'
        ? 'Source: unknown'
        : label.kind === 'none'
          ? 'Source: none'
          : label.kind === 'listError'
            ? 'Source (name couldn’t load)'
            : `Source (${label.kind === 'unavailable' ? 'unavailable' : label.kind === 'error' ? 'couldn’t load' : 'loading'})`;

  const close = (returnFocus: boolean) => {
    setPlacement(null);
    if (returnFocus) triggerRef.current?.focus();
  };

  useDismissibleLayer({
    open,
    onDismiss: (reason) => close(reason === 'escape'),
    refs: [triggerRef, menuRef],
  });

  // Focus the first item once the menu is in the DOM.
  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>('[role^="menuitem"]:not([aria-disabled="true"])')?.focus();
  }, [open]);

  const openMenu = (refresh: boolean) => {
    if (!triggerRef.current || key == null) return;
    // Opening re-reads the list: that is also the retry for a failed one.
    if (refresh) controller.refreshDictionary();
    setPlacement({ ...placeMenu(triggerRef.current), key, generation: controller.store.getState().generation });
  };

  const toggle = () => {
    if (!target || !triggerRef.current) return;
    if (open) return close(false);
    if (label.kind === 'error') return controller.retryRead(target);
    // Only this card's OWN assignment still loading (or a save in flight)
    // blocks the menu. A name still loading does not: opening re-reads the
    // list, so a hung list read can never leave the card un-clickable.
    if (entry?.status !== 'ready' || writing) return;
    openMenu(true);
  };

  const addNew = () => {
    close(false);
    openAddSource({
      // The trigger may have unmounted (card gone) by the time the dialog closes.
      onAdded: () => {
        if (triggerRef.current?.isConnected) openMenu(false);
      },
      onCancelled: () => triggerRef.current?.focus(),
    });
  };

  const choose = (sourceId: string | null, detail: number) => {
    close(true);
    if (detail > 0) swallowFollowUpClick();
    if (target) void controller.assign(target, sourceId);
  };

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = [
      ...(menuRef.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([aria-disabled="true"])') ?? []),
    ];
    const at = items.indexOf(document.activeElement as HTMLElement);
    const move = (to: number) => items[(to + items.length) % items.length]?.focus();
    if (e.key === 'ArrowDown') move(at + 1);
    else if (e.key === 'ArrowUp') move(at - 1);
    else if (e.key === 'Home') move(0);
    else if (e.key === 'End') move(items.length - 1);
    else if (e.key === 'Escape') close(true); // stopped below, so the layer hook never sees it
    // Back to the trigger first, so Tab continues from the card, not from the
    // end of the page where the portalled menu lives.
    else if (e.key === 'Tab') return close(true);
    else return;
    e.preventDefault();
  };

  const sorted = known ? sortSources(known) : [];
  const itemClass =
    'text-foreground hover:bg-muted focus:bg-muted flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs outline-none';

  const menu =
    open &&
    placement &&
    createPortal(
      <div
        ref={menuRef}
        id={menuId}
        role="menu"
        tabIndex={-1}
        aria-label="Choose a source"
        data-card-source-menu=""
        className="bg-card border-border fixed z-[100] w-56 overflow-y-auto rounded-lg border py-1 shadow-lg"
        style={{ left: placement.left, top: placement.top, bottom: placement.bottom, maxHeight: placement.maxHeight }}
        // A portal still bubbles through the React tree to the card: stop
        // every pointer and key event here so nothing opens, pins or trades.
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          onMenuKeyDown(e);
        }}
      >
        {issue && (
          <p role="alert" className="mx-2 mb-1 rounded bg-amber-400/10 px-2 py-1.5 text-xs text-amber-300 light:text-amber-700">
            {ISSUE_TEXT[issue]}
          </p>
        )}
        <button type="button" role="menuitem" aria-haspopup="dialog" className={itemClass} onClick={addNew}>
          <Plus size={12} aria-hidden="true" className="shrink-0" />
          Add New
        </button>
        {dictionary.status === 'error' && (
          <button
            type="button"
            role="menuitem"
            className={`${itemClass} text-amber-300 light:text-amber-700`}
            onClick={() => {
              // The item vanishes while the list reloads: keep focus in the menu.
              menuRef.current?.focus();
              controller.refreshDictionary();
            }}
          >
            Couldn’t load your sources — retry
          </button>
        )}
        {known == null && dictionary.status === 'loading' && (
          <p role="status" className="text-muted-foreground px-3 py-1.5 text-xs">
            Loading sources…
          </p>
        )}
        {known != null && sorted.length === 0 && (
          <p role="status" className="text-muted-foreground px-3 py-1.5 text-xs">
            No sources yet
          </p>
        )}
        {sorted.map((s) => (
          <button
            key={s.id}
            type="button"
            role="menuitemradio"
            aria-checked={s.id === assignedId}
            title={s.name}
            className={itemClass}
            onClick={(e) => choose(s.id, e.detail)}
          >
            <Check
              size={12}
              aria-hidden="true"
              className={`shrink-0 ${s.id === assignedId ? 'text-purple-400 light:text-purple-700' : 'invisible'}`}
            />
            <span className="min-w-0 truncate">{s.name}</span>
          </button>
        ))}
        <div role="separator" className="border-border my-1 border-t" />
        <button
          type="button"
          role="menuitem"
          aria-disabled={assignedId === null}
          className={`${itemClass} aria-disabled:text-muted-foreground aria-disabled:cursor-not-allowed`}
          onClick={(e) => {
            if (assignedId !== null) choose(null, e.detail);
          }}
        >
          Clear
        </button>
      </div>,
      document.body,
    );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-lt2-owner=""
        data-card-source-control=""
        // aria-disabled, not disabled: a keyboard user still reaches the control
        // and hears why it is unavailable (toggle() refuses without a target).
        aria-disabled={!target || label.kind === 'unavailable' || undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-busy={label.kind === 'loading' || writing}
        aria-label={ariaLabel}
        title={title}
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        // ONE width in every state (#615's constant-width rule): choosing,
        // clearing, loading or failing never changes the header layout, so a
        // card cannot jump height merely because its source changed — the rule
        // #603 set for this header. 5rem fits the default and status labels
        // ("Source", "Unknown", "Not loaded"); longer names truncate with the
        // full name in title/aria. On narrow cards the controls take their own
        // header row (.g1-annotated-header, globals.css), where the button may
        // also shrink toward 3.5rem — e.g. mini cards from 144px.
        className={`border-border inline-flex w-[5rem] min-w-[3.5rem] shrink items-center justify-between gap-1 rounded border px-1.5 py-0.5 text-[11px] leading-4 transition-colors aria-disabled:cursor-not-allowed aria-disabled:opacity-40 aria-disabled:hover:border-border aria-disabled:hover:text-muted-foreground ${
          label.kind === 'named'
            ? 'text-foreground hover:border-purple-400/60'
            : label.kind === 'error' || label.kind === 'listError' || label.kind === 'unknown' || issue
              ? 'border-amber-400/50 text-amber-300 light:border-amber-600/60 light:text-amber-700'
              : 'text-muted-foreground hover:text-foreground'
        } ${label.kind === 'loading' || writing ? 'opacity-60' : ''} ${open ? 'border-purple-400/60 bg-purple-400/10' : ''}`}
      >
        <span className="min-w-0 truncate">{buttonText}</span>
        {label.kind === 'error' || label.kind === 'listError' ? (
          <span aria-hidden="true" className="font-bold">
            !
          </span>
        ) : (
          <ChevronDown size={10} aria-hidden="true" className="shrink-0" />
        )}
      </button>
      {menu}
    </>
  );
}
